import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Phone, ChevronDown, User } from "lucide-react"
import { useAvailableUsers } from "@/hooks/useAvailableUsers"
import { useCreateDirectConversation } from "@/hooks/useDirectConversation"
import { useUserPresence } from "@/hooks/useUserPresence"
import { toast } from "sonner"

interface PorteiroDropdownProps {
  onConversationCreated: (conversationId: string, recipientName: string) => void
}

export const PorteiroDropdown = ({ onConversationCreated }: PorteiroDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { data: users, isLoading } = useAvailableUsers()
  const createConversation = useCreateDirectConversation()
  const { isUserOnline } = useUserPresence()

  // Filtrar apenas porteiros
  const porteiros = users?.filter(user => user.role === 'porteiro') || []

  const handleStartConversation = async (userId: string, userName: string) => {
    try {
      const conversationId = await createConversation.mutateAsync({
        participant_user_id: userId
      })
      onConversationCreated(conversationId, userName)
      setIsOpen(false)
      toast.success(`Conversa iniciada com ${userName}`)
    } catch (error) {
      console.error('Erro ao criar conversa:', error)
      toast.error('Erro ao iniciar conversa')
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }


  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          className="w-full justify-between h-16"
        >
          <div className="flex items-center">
            <Phone className="h-6 w-6 mr-4" />
            <div className="text-left">
              <p className="font-medium">Portaria</p>
              <p className="text-sm text-muted-foreground">
                Falar com a portaria
              </p>
            </div>
          </div>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="start">
        <DropdownMenuLabel>Selecione um porteiro</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {isLoading ? (
          <div className="p-4 text-center text-muted-foreground">
            Carregando porteiros...
          </div>
        ) : porteiros.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            Nenhum porteiro disponível
          </div>
        ) : (
          porteiros.map((porteiro) => {
            const isOnline = isUserOnline(porteiro.id)
            return (
              <DropdownMenuItem
                key={porteiro.id}
                className="p-3 cursor-pointer"
                onClick={() => handleStartConversation(porteiro.id, porteiro.full_name || 'Porteiro')}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="relative">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src="" />
                      <AvatarFallback className="bg-muted">
                        <User className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    {/* Indicador de status online */}
                    <div 
                      className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-background ${
                        isOnline ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                    />
                  </div>
                  
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {porteiro.full_name || 'Porteiro'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge 
                        variant={isOnline ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {isOnline ? 'Online' : 'Offline'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DropdownMenuItem>
            )
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}